import React, { useCallback, useMemo } from "react";

import dayjs from "dayjs";
import qs from "query-string";

import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Accordion from "react-bootstrap/Accordion";
import Card from "react-bootstrap/Card";
import { useTranslation } from "react-i18next";
import useToggle from "react-use/lib/useToggle";

import * as yup from "yup";

import { toast } from "react-toastify";
import { useMutation, useQuery } from "@apollo/client";
import { useHistory, Link, useParams } from "react-router-dom";

import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import TextArea from "../../../../components/Textarea";
import Input from "../../../../components/Input";
import SwitchToAppOnly from "./SwitchToAppOnly";
import SyncStatus from "./SyncStatus";

import { QUERY_ACTIVE_DIRECTORY } from "../../../../config/graphql/query";
import {
  MUTATION_UPDATE_ACTIVE_DIRECTORY,
  MUTATION_DELETE_ACTIVE_DIRECTORY_INTEGRATION,
} from "../../../../config/graphql/mutation";

import { useClientId } from "../hooks/useClient";
import { getApiUrl } from "../../../../lib/common/getApiUrl";

/** Counterpart of `Outlook/Integration`, plus the last sync and both switches. */
const ActiveDirectoryIntegration = React.memo(() => {
  const [show, setShow] = useToggle(false);
  const { t } = useTranslation(["client", "common"]);

  const history = useHistory();
  const { activeDirectoryId } = useParams<{ activeDirectoryId: string }>();

  const clientId = useClientId();

  const schema = useMemo(
    () =>
      yup.object().shape({
        title: yup.string().nullable(),
        description: yup.string().nullable(),
      }),
    [],
  );

  const methods = useForm({
    resolver: yupResolver(schema),
    shouldFocusError: false,
    shouldUnregister: true,
    defaultValues: {
      title: "",
      description: "",
    },
  });

  const context = { headers: { ClientId: clientId } };

  const [onUpdate] = useMutation(MUTATION_UPDATE_ACTIVE_DIRECTORY, { context });
  const [onDelete] = useMutation(MUTATION_DELETE_ACTIVE_DIRECTORY_INTEGRATION, {
    context,
  });

  const { data } = useQuery<{ activeDirectory: IActiveDirectory }>(
    QUERY_ACTIVE_DIRECTORY,
    {
      skip: !activeDirectoryId,
      variables: { id: activeDirectoryId },
      fetchPolicy: "network-only",
      context,
      onCompleted: ({ activeDirectory }) => {
        methods.reset({
          title: activeDirectory?.title ?? "",
          description: activeDirectory?.description ?? "",
        });
      },
    },
  );

  const activeDirectory = data?.activeDirectory;
  const isAppOnly = activeDirectory?.authMode === "appOnly";

  const apiUrl = getApiUrl();

  // Carries the id, so the server updates this integration instead of creating one.
  const signInAgainURL = useMemo(
    () =>
      qs.stringifyUrl(
        {
          url: `${apiUrl}/api/v1/activedirectory/auth/login`,
          query: {
            client: clientId,
            activeDirectory: activeDirectoryId,
            redirectUri: window.location.href,
          },
        },
        { encode: true },
      ),
    [apiUrl, clientId, activeDirectoryId],
  );

  const listUrl = `/clients/${clientId}/integrations/activedirectory`;

  const onRemove = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      return onDelete({ variables: { id: activeDirectoryId } })
        .then(() => {
          history.replace(listUrl);
          toast.success<string>(
            t("client:client.integrationForm.toast.deleted"),
          );
        })
        .catch((error) => {
          toast.error<string>(
            error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
          );
        });
    },
    [activeDirectoryId, history, listUrl, onDelete, t],
  );

  const onBeforeRemove = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();

      setShow(true);
    },
    [setShow],
  );

  const onSubmit = ({ ...rest }) => {
    if (!activeDirectoryId) {
      return;
    }

    onUpdate({
      variables: { input: { ...rest, id: activeDirectoryId } },
    })
      .then(() => {
        toast.success<string>(t("client:client.integrationForm.toast.updated"));
      })
      .catch((error) => {
        toast.error<string>(
          error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
        );
      });
  };

  return (
    <div className="container-fluid">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb my-3">
          <li className="breadcrumb-item active" aria-current="page">
            <Link to="/clients">
              {t("client:client.integrationForm.nav.clients")}
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            <Link to={`/clients/${clientId}`}>
              {activeDirectory?.client?.title ?? "Client"}
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            <Link to={listUrl}>
              {t("client:client.clientCreate.nav.activeDirectory")}
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {activeDirectory?.title ||
              activeDirectory?.name ||
              activeDirectory?.email}
          </li>
        </ol>
      </nav>
      <FormProvider {...methods}>
        <form className="row mb-3" onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="col">
            <div className="form-group">
              <label htmlFor="title">
                {t("client:client.integrationForm.form.title")}
              </label>
              <Input name="title" className="form-control" />
            </div>
            <div className="form-group">
              <label htmlFor="description">
                {t("client:client.integrationForm.form.description")}
              </label>
              <TextArea name="description" className="form-control" />
            </div>
          </div>
          <div className="col-12 d-flex align-items-center">
            <input type="submit" className="btn btn-primary" />
            {activeDirectoryId && (
              <button onClick={onBeforeRemove} className="btn btn-danger ml-3">
                {t("common:delete")}
              </button>
            )}
            {activeDirectory && (
              <span className="ml-auto d-flex align-items-center">
                <a href={signInAgainURL} className="btn btn-link mr-2">
                  {t(
                    `client:client.integrations.activeDirectory.button.${
                      isAppOnly ? "backToDelegated" : "signInAgain"
                    }`,
                  )}
                </a>
                <SwitchToAppOnly
                  clientId={clientId as string}
                  activeDirectory={activeDirectory}
                />
              </span>
            )}
          </div>
        </form>
        <Accordion defaultActiveKey="0" className="mb-3">
          <Card>
            <Accordion.Toggle as={Card.Header} eventKey="0">
              {t("client:client.integrationForm.form.information")}
            </Accordion.Toggle>

            <Accordion.Collapse eventKey="0">
              <Card.Body>
                {t("client:client.integrationForm.form.mode")}
                {t(
                  `client:client.integrations.activeDirectory.mode.${
                    isAppOnly ? "appOnly" : "delegated"
                  }`,
                )}
                <hr />
                {/* The two modes are keyed by different things: delegated by the
                    signed-in account, app-only by the customer's tenant. */}
                {isAppOnly ? (
                  <>
                    {t("client:client.integrationForm.form.tenantId")}
                    {activeDirectory?.tenantId ?? ""}
                    <hr />
                    {t(
                      "client:client.integrations.activeDirectory.form.adminEmail",
                    )}
                    {activeDirectory?.email ?? ""}
                  </>
                ) : (
                  <>
                    {t("client:client.integrationForm.form.name")}
                    {activeDirectory?.name ?? ""}
                    <hr />
                    {t("client:client.integrationForm.form.email")}
                    {activeDirectory?.email ?? ""}
                  </>
                )}
                <hr />
                {t("client:client.integrations.activeDirectory.form.lastSync")}
                {activeDirectory && <SyncStatus item={activeDirectory} />}
                {!!activeDirectory?.failedAttempts &&
                  activeDirectory?.lastSyncError && (
                    <div className="alert alert-danger small mt-2 mb-0">
                      {activeDirectory.lastSyncError}
                    </div>
                  )}
              </Card.Body>
            </Accordion.Collapse>
          </Card>
          <Card>
            <Accordion.Toggle as={Card.Header} eventKey="1">
              {t("client:client.integrationForm.form.details")}
            </Accordion.Toggle>

            <Accordion.Collapse eventKey="1">
              <Card.Body>
                {t("client:client.integrationForm.form.created")}
                {activeDirectory?.createdAt
                  ? dayjs(activeDirectory.createdAt).format("L LT")
                  : ""}
                <hr />
                {t("client:client.integrationForm.form.permissions")}
                {(activeDirectory?.permissions ?? []).join(", ")}
                {!isAppOnly && (
                  <>
                    <hr />
                    {t("client:client.integrationForm.form.account")}
                    {activeDirectory?.homeAccountId ?? ""}
                  </>
                )}
              </Card.Body>
            </Accordion.Collapse>
          </Card>
        </Accordion>
      </FormProvider>
      <Modal show={show} onHide={setShow} backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>
            {t("client:client.integrations.activeDirectory.modal.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("client:client.integrations.activeDirectory.modal.body")}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={setShow}>
            {t("common:cancel")}
          </Button>
          <Button variant="danger" onClick={onRemove}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

export default ActiveDirectoryIntegration;
